/**
 * FlexCOA Error Code System
 * 
 * Error Code Format: [NUMBER] (4-digit numeric code)
 * 
 * Error Code Ranges:
 * - 0001-0999: Survey-related errors
 * - 1000-1999: API communication errors
 * - 2000-2999: Authentication/Authorization errors
 * - 3000-3999: Data validation errors
 * - 4000-4999: System/Server errors
 * - 5000-5999: Network connectivity errors
 */

export const ERROR_CODES = {
  // Survey Errors (0001-0999)
  SURVEY: {
    NO_QUESTIONS: {
      code: '0001',
      title: 'No Questions Found',
      message: 'This survey has no questions available.',
      userMessage: 'This survey does not contain any questions. Please contact support.',
      severity: 'error',
      category: 'SURVEY'
    },
    NO_ASSIGNMENT: {
      code: '0002',
      title: 'Assignment Not Found',
      message: 'Survey assignment not found or does not belong to participant.',
      userMessage: 'We could not find your survey assignment. Please contact support.',
      severity: 'error',
      category: 'SURVEY'
    },
    NO_WINDOW: {
      code: '0003',
      title: 'No Survey Window',
      message: 'Survey assignment has no associated window.',
      userMessage: 'This survey assignment is not properly configured. Please contact support.',
      severity: 'error',
      category: 'SURVEY'
    },
    INVALID_QUESTION: {
      code: '0004',
      title: 'Invalid Question',
      message: 'Question does not belong to this survey.',
      userMessage: 'There was an error loading the question. Please try again or contact support.',
      severity: 'error',
      category: 'SURVEY'
    },
    RESPONSE_VALIDATION_FAILED: {
      code: '0005',
      title: 'Invalid Response',
      message: 'Response validation failed.',
      userMessage: 'Your response could not be validated. Please check your answer and try again.',
      severity: 'warning',
      category: 'SURVEY'
    },
    SAVE_FAILED: {
      code: '0006',
      title: 'Save Failed',
      message: 'Failed to save survey response.',
      userMessage: 'We could not save your response. Please try again.',
      severity: 'error',
      category: 'SURVEY'
    },
    LOAD_FAILED: {
      code: '0007',
      title: 'Load Failed',
      message: 'Failed to load survey question.',
      userMessage: 'We could not load the survey question. Please refresh the page and try again.',
      severity: 'error',
      category: 'SURVEY'
    },
    PROGRESS_ERROR: {
      code: '0008',
      title: 'Progress Error',
      message: 'Failed to fetch survey progress.',
      userMessage: 'We could not load your survey progress. This will not affect your responses.',
      severity: 'warning',
      category: 'SURVEY'
    },
    INVALID_DATA: {
      code: '0009',
      title: 'Invalid Survey Data',
      message: 'Invalid question data received from server.',
      userMessage: 'The survey data is invalid. Please refresh the page or contact support.',
      severity: 'error',
      category: 'SURVEY'
    },
    NO_OPTIONS: {
      code: '0010',
      title: 'No Options Available',
      message: 'Question has no options configured.',
      userMessage: 'This question is missing answer options. Please contact support.',
      severity: 'error',
      category: 'SURVEY'
    },
    UNSUPPORTED_TYPE: {
      code: '0011',
      title: 'Unsupported Question Type',
      message: 'Question type is not supported.',
      userMessage: 'This question type is not supported. Please contact support.',
      severity: 'error',
      category: 'SURVEY'
    },
    LINKED_SURVEY_ERROR: {
      code: '0012',
      title: 'Linked Survey Error',
      message: 'Error checking for linked surveys.',
      userMessage: 'There was an error checking for linked surveys. Please refresh the page.',
      severity: 'warning',
      category: 'SURVEY'
    },
    REQUIRED_ANSWER: {
      code: '0013',
      title: 'Answer Required',
      message: 'This question requires an answer.',
      userMessage: 'Please provide an answer to continue.',
      severity: 'warning',
      category: 'SURVEY'
    },
    PREVIOUS_QUESTION_ERROR: {
      code: '0014',
      title: 'Navigation Error',
      message: 'Failed to load previous question.',
      userMessage: 'We could not load the previous question. Please try again.',
      severity: 'error',
      category: 'SURVEY'
    },
    SEQUENTIAL_NAVIGATION_ERROR: {
      code: '0015',
      title: 'Navigation Error',
      message: 'Failed to load next sequential question.',
      userMessage: 'We could not load the next question. Please try again.',
      severity: 'error',
      category: 'SURVEY'
    }
  },

  // API Errors (1000-1999)
  API: {
    REQUEST_FAILED: {
      code: '1000',
      title: 'Request Failed',
      message: 'API request failed.',
      userMessage: 'The request to the server failed. Please check your connection and try again.',
      severity: 'error',
      category: 'API'
    },
    TIMEOUT: {
      code: '1001',
      title: 'Request Timeout',
      message: 'API request timed out.',
      userMessage: 'The request took too long to complete. Please try again.',
      severity: 'error',
      category: 'API'
    },
    BAD_REQUEST: {
      code: '1002',
      title: 'Bad Request',
      message: 'Invalid request sent to server.',
      userMessage: 'There was an error with your request. Please try again or contact support.',
      severity: 'error',
      category: 'API'
    },
    RESPONSE_PARSE_ERROR: {
      code: '1003',
      title: 'Response Error',
      message: 'Failed to parse server response.',
      userMessage: 'We received an invalid response from the server. Please try again.',
      severity: 'error',
      category: 'API'
    }
  },

  // Authentication/Authorization Errors (2000-2999)
  AUTH: {
    NOT_AUTHENTICATED: {
      code: '2000',
      title: 'Not Authenticated',
      message: 'User is not authenticated.',
      userMessage: 'Your session has expired. Please log in again.',
      severity: 'error',
      category: 'AUTH'
    },
    NOT_AUTHORIZED: {
      code: '2001',
      title: 'Not Authorized',
      message: 'User is not authorized to perform this action.',
      userMessage: 'You do not have permission to perform this action.',
      severity: 'error',
      category: 'AUTH'
    },
    INVALID_CREDENTIALS: {
      code: '2002',
      title: 'Invalid Credentials',
      message: 'Invalid username or password.',
      userMessage: 'Your credentials are invalid. Please try again.',
      severity: 'error',
      category: 'AUTH'
    },
    TOKEN_EXPIRED: {
      code: '2003',
      title: 'Session Expired',
      message: 'Authentication token has expired.',
      userMessage: 'Your session has expired. Please log in again.',
      severity: 'warning',
      category: 'AUTH'
    },
    WRONG_USER_TYPE: {
      code: '2004',
      title: 'Wrong User Type',
      message: 'User type does not match required type for this action.',
      userMessage: 'You do not have the correct permissions to access this feature.',
      severity: 'error',
      category: 'AUTH'
    }
  },

  // Data Validation Errors (3000-3999)
  DATA: {
    VALIDATION_ERROR: {
      code: '3000',
      title: 'Validation Error',
      message: 'Data validation failed.',
      userMessage: 'Please check your input and try again.',
      severity: 'warning',
      category: 'DATA'
    },
    MISSING_REQUIRED_FIELD: {
      code: '3001',
      title: 'Missing Required Field',
      message: 'Required field is missing.',
      userMessage: 'Please fill in all required fields.',
      severity: 'warning',
      category: 'DATA'
    },
    INVALID_FORMAT: {
      code: '3002',
      title: 'Invalid Format',
      message: 'Data format is invalid.',
      userMessage: 'The data format is invalid. Please check your input.',
      severity: 'warning',
      category: 'DATA'
    },
    OUT_OF_RANGE: {
      code: '3003',
      title: 'Out of Range',
      message: 'Value is out of acceptable range.',
      userMessage: 'The value you entered is out of range. Please enter a valid value.',
      severity: 'warning',
      category: 'DATA'
    },
    INVALID_CHOICE: {
      code: '3004',
      title: 'Invalid Choice',
      message: 'Selected choice is not valid.',
      userMessage: 'Your selection is invalid. Please choose a valid option.',
      severity: 'warning',
      category: 'DATA'
    }
  },

  // System/Server Errors (4000-4999)
  SYSTEM: {
    INTERNAL_ERROR: {
      code: '4000',
      title: 'Internal Server Error',
      message: 'An internal server error occurred.',
      userMessage: 'An unexpected error occurred. Our team has been notified. Please try again later.',
      severity: 'error',
      category: 'SYSTEM'
    },
    SERVICE_UNAVAILABLE: {
      code: '4001',
      title: 'Service Unavailable',
      message: 'Service is temporarily unavailable.',
      userMessage: 'The service is temporarily unavailable. Please try again in a few minutes.',
      severity: 'error',
      category: 'SYSTEM'
    },
    DATABASE_ERROR: {
      code: '4002',
      title: 'Database Error',
      message: 'Database operation failed.',
      userMessage: 'A database error occurred. Please try again or contact support.',
      severity: 'error',
      category: 'SYSTEM'
    },
    CONFIGURATION_ERROR: {
      code: '4003',
      title: 'Configuration Error',
      message: 'System configuration error.',
      userMessage: 'There is a configuration error. Please contact support.',
      severity: 'error',
      category: 'SYSTEM'
    }
  },

  // Network Errors (5000-5999)
  NETWORK: {
    CONNECTION_ERROR: {
      code: '5000',
      title: 'Connection Error',
      message: 'Network connection failed.',
      userMessage: 'Could not connect to the server. Please check your internet connection.',
      severity: 'error',
      category: 'NETWORK'
    },
    OFFLINE: {
      code: '5001',
      title: 'Offline',
      message: 'Device is offline.',
      userMessage: 'You appear to be offline. Please check your internet connection.',
      severity: 'error',
      category: 'NETWORK'
    },
    SLOW_CONNECTION: {
      code: '5002',
      title: 'Slow Connection',
      message: 'Network connection is slow.',
      userMessage: 'Your connection is slow. Some features may not work properly.',
      severity: 'warning',
      category: 'NETWORK'
    }
  }
};

/**
 * Get error details by error code
 * @param {string} errorCode - Error code (e.g., '0001', '1000', '2000')
 * @returns {Object|null} Error details or null if not found
 */
export const getErrorByCode = (errorCode) => {
  for (const category in ERROR_CODES) {
    for (const key in ERROR_CODES[category]) {
      if (ERROR_CODES[category][key].code === errorCode) {
        return ERROR_CODES[category][key];
      }
    }
  }
  return null;
};

/**
 * Create a standardized error object
 * @param {Object} errorConfig - Error configuration from ERROR_CODES
 * @param {Object} additionalContext - Additional context (optional)
 * @returns {Object} Standardized error object
 */
export const createErrorObject = (errorConfig, additionalContext = {}) => {
  return {
    code: errorConfig.code,
    title: errorConfig.title,
    message: errorConfig.message,
    userMessage: errorConfig.userMessage,
    severity: errorConfig.severity,
    category: errorConfig.category,
    timestamp: new Date().toISOString(),
    ...additionalContext
  };
};

/**
 * Map a backend error message to an error code
 * @param {string} errorMessage - Error message from backend
 * @returns {Object} Error object with code
 */
export const mapBackendError = (errorMessage) => {
  const messageLower = errorMessage?.toLowerCase() || '';
  
  // Survey-related errors
  if (messageLower.includes('no questions') || messageLower.includes('has no questions')) {
    return createErrorObject(ERROR_CODES.SURVEY.NO_QUESTIONS, { originalMessage: errorMessage });
  }
  if (messageLower.includes('assignment not found') || messageLower.includes('does not belong')) {
    return createErrorObject(ERROR_CODES.SURVEY.NO_ASSIGNMENT, { originalMessage: errorMessage });
  }
  if (messageLower.includes('no associated window') || messageLower.includes('no window')) {
    return createErrorObject(ERROR_CODES.SURVEY.NO_WINDOW, { originalMessage: errorMessage });
  }
  if (messageLower.includes('does not belong to this survey') || messageLower.includes('invalid question')) {
    return createErrorObject(ERROR_CODES.SURVEY.INVALID_QUESTION, { originalMessage: errorMessage });
  }
  if (messageLower.includes('validation failed') || messageLower.includes('invalid response')) {
    return createErrorObject(ERROR_CODES.SURVEY.RESPONSE_VALIDATION_FAILED, { originalMessage: errorMessage });
  }
  if (messageLower.includes('failed to save') || messageLower.includes('saving')) {
    return createErrorObject(ERROR_CODES.SURVEY.SAVE_FAILED, { originalMessage: errorMessage });
  }
  if (messageLower.includes('failed to load') || messageLower.includes('loading')) {
    return createErrorObject(ERROR_CODES.SURVEY.LOAD_FAILED, { originalMessage: errorMessage });
  }
  if (messageLower.includes('no options')) {
    return createErrorObject(ERROR_CODES.SURVEY.NO_OPTIONS, { originalMessage: errorMessage });
  }
  if (messageLower.includes('unsupported question type') || messageLower.includes('unsupported type')) {
    return createErrorObject(ERROR_CODES.SURVEY.UNSUPPORTED_TYPE, { originalMessage: errorMessage });
  }
  if (messageLower.includes('requires an answer') || messageLower.includes('required')) {
    return createErrorObject(ERROR_CODES.SURVEY.REQUIRED_ANSWER, { originalMessage: errorMessage });
  }
  if (messageLower.includes('previous question')) {
    return createErrorObject(ERROR_CODES.SURVEY.PREVIOUS_QUESTION_ERROR, { originalMessage: errorMessage });
  }
  
  // Auth errors
  if (messageLower.includes('not authenticated') || messageLower.includes('authentication')) {
    return createErrorObject(ERROR_CODES.AUTH.NOT_AUTHENTICATED, { originalMessage: errorMessage });
  }
  if (messageLower.includes('not authorized') || messageLower.includes('permission')) {
    return createErrorObject(ERROR_CODES.AUTH.NOT_AUTHORIZED, { originalMessage: errorMessage });
  }
  if (messageLower.includes('only participants') || messageLower.includes('user type')) {
    return createErrorObject(ERROR_CODES.AUTH.WRONG_USER_TYPE, { originalMessage: errorMessage });
  }
  
  // Data validation errors
  if (messageLower.includes('invalid') && (messageLower.includes('choice') || messageLower.includes('selection'))) {
    return createErrorObject(ERROR_CODES.DATA.INVALID_CHOICE, { originalMessage: errorMessage });
  }
  if (messageLower.includes('format')) {
    return createErrorObject(ERROR_CODES.DATA.INVALID_FORMAT, { originalMessage: errorMessage });
  }
  if (messageLower.includes('out of range') || messageLower.includes('between')) {
    return createErrorObject(ERROR_CODES.DATA.OUT_OF_RANGE, { originalMessage: errorMessage });
  }
  
  // Network errors
  if (messageLower.includes('network') || messageLower.includes('connection')) {
    return createErrorObject(ERROR_CODES.NETWORK.CONNECTION_ERROR, { originalMessage: errorMessage });
  }
  if (messageLower.includes('timeout')) {
    return createErrorObject(ERROR_CODES.API.TIMEOUT, { originalMessage: errorMessage });
  }
  
  // System errors
  if (messageLower.includes('internal server error') || messageLower.includes('500')) {
    return createErrorObject(ERROR_CODES.SYSTEM.INTERNAL_ERROR, { originalMessage: errorMessage });
  }
  if (messageLower.includes('service unavailable') || messageLower.includes('503')) {
    return createErrorObject(ERROR_CODES.SYSTEM.SERVICE_UNAVAILABLE, { originalMessage: errorMessage });
  }
  
  // Default to generic API error
  return createErrorObject(ERROR_CODES.API.REQUEST_FAILED, { 
    originalMessage: errorMessage,
    userMessage: errorMessage || 'An unexpected error occurred. Please try again or contact support.'
  });
};

/**
 * Format error for logging
 * @param {Object} error - Error object
 * @returns {string} Formatted error string
 */
export const formatErrorForLogging = (error) => {
  return `[${error.code}] ${error.title}: ${error.message} | User Message: ${error.userMessage}`;
};

export default ERROR_CODES;
